import { action, computed, makeObservable, observable } from 'mobx'

import { ISitePermitStatusChange } from '~/client/graph'
import PermitStatusChange from '~/client/src/shared/models/PermitStatusChange'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

export default class PermitStatusChangesStore {
  @observable public isDataReceived = false
  // The list and its subscription serve one form at a time. Forms stack (a linked workflow
  // previewed over the one open in Logistics), so the latest live one is served.
  private owners: Array<{ token: number; formId: string }> = []
  private nextOwnerToken = 1

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get list() {
    return Array.from(this.byId.values())
  }

  public get byId() {
    return this.eventsStore.appState.permitStatusChanges
  }

  /** Serves this form's status changes until released; returns the release token. */
  public listenFor = (formId: string): number => {
    const token = this.nextOwnerToken++
    this.owners.push({ token, formId })
    this.serveLatestOwner()
    return token
  }

  public stopListening = (token: number) => {
    const index = this.owners.findIndex(owner => owner.token === token)
    if (index === -1) return
    const wasServed = index === this.owners.length - 1
    this.owners.splice(index, 1)
    if (wasServed) this.serveLatestOwner()
  }

  /**
   * Whether a response loaded for this form may still be applied. A request can land after
   * its form closed or another opened over it; its list and subscription are then dropped.
   */
  public isServing = (formId: string): boolean =>
    this.owners[this.owners.length - 1]?.formId === formId

  private serveLatestOwner() {
    this.dontListenToStatusChanges()
    const latest = this.owners[this.owners.length - 1]
    if (latest) void this.loadAndListenStatusChangesByFormId(latest.formId)
  }

  private loadAndListenStatusChangesByFormId = (
    formId: string,
  ): Promise<void> => {
    return new Promise(resolve => {
      this.eventsStore.dispatch(
        e.LOAD_AND_LISTEN_TO_SITE_PERMIT_STATUS_CHANGES,
        formId,
        resolve,
      )
    })
  }

  private dontListenToStatusChanges = () => {
    this.graphExecutorStore.terminateSubscription(
      e.LISTEN_TO_SITE_PERMIT_STATUS_CHANGES,
    )
    this.clearList()
  }

  @action.bound
  public receiveList(list: ISitePermitStatusChange[]) {
    this.clearList()

    this.updateFromList(list)

    this.isDataReceived = true
  }

  private updateFromList(list: ISitePermitStatusChange[]) {
    list.forEach(dto => this.receiveOne(dto.id, dto))
  }

  @action.bound
  public receiveOne(id: string, dto: ISitePermitStatusChange) {
    if (dto) {
      const statusChange = PermitStatusChange.fromDto(dto)
      this.byId.set(statusChange.id, statusChange)
    } else {
      this.byId.delete(id)
    }
  }

  public getStatusChangesForPermit = (
    permitId: string,
  ): PermitStatusChange[] => {
    return this.list.filter(sc => sc.permitId === permitId)
  }

  @action.bound
  private clearList() {
    this.byId.clear()
    this.isDataReceived = false
  }
}
